import { isEmpty } from 'lodash/fp';
import { titleCase } from 'title-case';

import {
  CheckMetadata,
  CheckMetadataItem,
  CheckMetadataItemType,
  renderMatchGroupResultsTooltip,
  renderMatchResultCell,
  renderMatchResultRowHeader,
  renderRequiredCell,
  renderStatusCell,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import Pii from 'dashboard/components/Pii';
import { CheckName, DatabaseVatNumberIdentityComparison } from 'dashboard/models/check';
import { VerificationDatabaseVatNumber } from 'dashboard/models/verification';
import { sortChecks } from 'lib/utils/checks';
import { VerificationStatus } from 'lib/verification';

const IDENTITY_COMPARISON_CHECK_NAMES = [CheckName.DatabaseVatNumberIdentityComparison];

// Note: The only check we use here is the DatabaseVatNumberIdentityComparison check. However, this component
// is still classified as a "multiple checks" component, because otherwise its row in the ChecksTable would not
// be expandable if it were redacted. This way, we can selectively redact the needed portions of the metadata,
// while still showing information like "Best Match Group."
export const computeDatabaseVatNumberAttributeChecksMetadata = (
  verification: VerificationDatabaseVatNumber,
): CheckMetadata => {
  const sortedChecks = sortChecks(verification.attributes?.checks || []);
  const isRedacted = verification.attributes?.['redacted-at'] != null;

  const identityComparisonCheck = sortedChecks.find((check) =>
    IDENTITY_COMPARISON_CHECK_NAMES.includes(check.name),
  ) as DatabaseVatNumberIdentityComparison;

  const checkResults = identityComparisonCheck?.metadata?.results ?? [];

  const comparisonMethods = checkResults
    .flatMap((result) => result['attribute-comparison-results'])
    .map((comparisonResult) => comparisonResult['comparison-type'])
    .filter((value, index, self) => self.indexOf(value) === index);

  const hasIdentityRecordData = checkResults.some(
    (result) => !isEmpty(result['normalized-extracted-attributes']),
  );
  const showRecordData =
    hasIdentityRecordData && verification.attributes.status === VerificationStatus.Passed;

  let rows: (React.ReactNode | string)[][] = [];

  if (checkResults) {
    rows = checkResults.map((result) => {
      const {
        properties,
        'normalized-claimed-attributes': normalizedClaimedAttributes,
        'normalized-extracted-attributes': normalizedExtractedAttributes,
        'attribute-comparison-results': attributeComparisonResults,
        'best-result': bestResult,
        required,
      } = result;

      const matchRequirementName = properties[0].replace(/_/g, ' ');

      const claimedAttribute = normalizedClaimedAttributes[0] ?? 'N/A';
      let extractedAttribute = '';
      if (showRecordData) {
        extractedAttribute = normalizedExtractedAttributes[0] ?? 'N/A';
      }

      return [
        titleCase(matchRequirementName),
        renderPropertyCell(claimedAttribute, isRedacted),
        ...(showRecordData ? [extractedAttribute] : []),
        renderMatchResultCell(bestResult, attributeComparisonResults),
        renderStatusCell(result.status),
        renderRequiredCell(required),
      ];
    });
  }

  const bestMatchGroupDescription = identityComparisonCheck?.metadata?.description;
  const matchGroupResults = verification.attributes['match-group-results'];
  const subheading = bestMatchGroupDescription ? (
    <div>
      {verification.attributes.status === VerificationStatus.Passed ? 'Best' : 'Closest'} Match
      Group: {bestMatchGroupDescription}
      {matchGroupResults &&
        matchGroupResults.length > 0 &&
        renderMatchGroupResultsTooltip(matchGroupResults)}
    </div>
  ) : undefined;

  return {
    metadata: [
      {
        type: CheckMetadataItemType.Table,
        heading: {
          title: 'Identity Comparison Check',
        },
        ...(subheading && { subheading }),
        rowHeaders: [
          'Property',
          'Input',
          ...(showRecordData ? ['Record'] : []),
          renderMatchResultRowHeader(comparisonMethods),
          'Status',
          'Required',
        ],
        rows,
        tableEmptyState: 'No comparison checks were performed for this verification.',
      },
    ] as CheckMetadataItem[],
  };
};

function renderPropertyCell(value: string, isRedacted: boolean) {
  return <Pii isRedacted={isRedacted}>{value}</Pii>;
}

export function getStatusForCheckRow(verification: VerificationDatabaseVatNumber) {
  const sortedChecks = sortChecks(verification.attributes?.checks || []);
  const identityComparisonCheck = sortedChecks.find((check) =>
    IDENTITY_COMPARISON_CHECK_NAMES.includes(check.name),
  ) as DatabaseVatNumberIdentityComparison;

  return identityComparisonCheck?.status;
}
